/* Shared visual primitives used by every detail page that follows the
   /previews style: cream bg, big teal gradient hero, sticky pill nav,
   bordered cards with gradient icon badges.
   Keep stateless so they can be imported anywhere. */
import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, XCircle, RotateCcw, Trophy } from 'lucide-react';
import { useSectionNav } from '../../hooks/useSectionNav';
import { useRevealOnce } from '../../hooks/useRevealOnce';

/* Pastel accent palette */
export const ACCENTS = {
  teal:    { card: 'border-teal-200 bg-teal-50/40',       icon: 'from-teal-400 to-emerald-500' },
  amber:   { card: 'border-amber-200 bg-amber-50/40',     icon: 'from-amber-400 to-orange-500' },
  rose:    { card: 'border-rose-200 bg-rose-50/40',       icon: 'from-rose-400 to-pink-500' },
  indigo:  { card: 'border-indigo-200 bg-indigo-50/40',   icon: 'from-indigo-400 to-violet-500' },
  emerald: { card: 'border-emerald-200 bg-emerald-50/40', icon: 'from-emerald-400 to-teal-500' },
  purple:  { card: 'border-purple-200 bg-purple-50/40',   icon: 'from-purple-400 to-fuchsia-500' },
  cyan:    { card: 'border-cyan-200 bg-cyan-50/40',       icon: 'from-cyan-400 to-sky-500' },
  blue:    { card: 'border-blue-200 bg-blue-50/40',       icon: 'from-blue-400 to-indigo-500' },
  // ── Per-path primary accents (used only by that path's detail page so the
  //    section icons visually match its hero. Additive; existing consumers
  //    reading `teal`/`emerald`/etc. are unaffected). ──
  banking_primary:    { card: 'border-emerald-300 bg-emerald-50/50', icon: 'from-emerald-500 to-green-700' },
  bluecollar_primary: { card: 'border-amber-300 bg-amber-50/50',     icon: 'from-amber-500 to-orange-600' },
  // Calm neutral used as the second tone in every path's 3-tone system
  // (hero-primary + neutral + a sparing highlight).
  slate:              { card: 'border-slate-200 bg-slate-50/50',     icon: 'from-slate-400 to-slate-600' },
};

export const Section = ({ id, icon: Icon, title, accent = 'teal', children }) => {
  const pal = ACCENTS[accent] || ACCENTS.teal;
  const revealRef = useRevealOnce();
  return (
    <section
      ref={revealRef}
      id={id}
      className={`reveal bg-white border-2 ${pal.card} rounded-3xl p-5 md:p-7 shadow-sm scroll-mt-52`}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${pal.icon} text-white flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon size={18} />
        </span>
        <h2 className="text-lg md:text-xl font-extrabold text-gray-900">{title}</h2>
      </div>
      <div className="text-gray-700 text-base leading-relaxed">{children}</div>
    </section>
  );
};

export const InfoCard = ({ label, children, tone = 'teal' }) => {
  const tones = {
    teal:    'bg-teal-50 border-teal-200',
    sky:     'bg-sky-50 border-sky-200',
    mint:    'bg-emerald-50 border-emerald-200',
    cream:   'bg-amber-50 border-amber-200',
    violet:  'bg-violet-50 border-violet-200',
    rose:    'bg-rose-50 border-rose-200',
    sun:     'bg-yellow-50 border-yellow-200',
  };
  const labelTones = {
    teal: 'text-teal-800', sky: 'text-sky-800', mint: 'text-emerald-800',
    cream: 'text-amber-800', violet: 'text-violet-800', rose: 'text-rose-800', sun: 'text-amber-800',
  };
  return (
    <div className={`rounded-2xl border ${tones[tone] || tones.teal} p-4 md:p-5`}>
      {label && <p className={`text-[11px] uppercase font-bold tracking-[0.18em] mb-1.5 ${labelTones[tone]}`}>{label}</p>}
      <div className="text-[15px] text-gray-800 leading-relaxed">{children}</div>
    </div>
  );
};

export const Bullets = ({ items, tone = 'teal' }) => {
  const tones = {
    teal: 'bg-teal-100 text-teal-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    rose: 'bg-rose-100 text-rose-700',
    emerald: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <ul className="space-y-2">
      {(items || []).map((x, i) => (
        <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed">
          <span className={`w-5 h-5 rounded-full ${tones[tone] || tones.teal} flex items-center justify-center mt-0.5 shrink-0`}>
            <ChevronRight size={12} />
          </span>
          <span>{x}</span>
        </li>
      ))}
    </ul>
  );
};

export const ChipList = ({ items, tone = 'teal' }) => {
  const tones = {
    teal: 'bg-teal-50 text-teal-800 border-teal-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    purple: 'bg-purple-50 text-purple-800 border-purple-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    rose: 'bg-rose-50 text-rose-800 border-rose-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  };
  return (
    <div className="stagger flex flex-wrap gap-2">
      {(items || []).map((x, i) => (
        <span key={i} className={`text-sm px-3 py-1.5 rounded-full border font-medium ${tones[tone] || tones.teal}`}>{x}</span>
      ))}
    </div>
  );
};

export const Accordion = ({ title, defaultOpen = false, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left px-4 md:px-5 py-3.5 hover:bg-gray-50 transition-colors"
      >
        <span className="font-bold text-gray-900 text-base">{title}</span>
        <ChevronDown size={18} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 md:px-5 pb-4 md:pb-5 text-gray-700 leading-relaxed">{children}</div>}
    </div>
  );
};

/* PillNav — horizontal sticky pill section nav (matches /previews).
   `hoverTone` picks the hover accent so each path can theme its nav to its hero
   (default keeps the legacy teal so other pages using PillNav are unaffected).
   Also: active pill auto-updates via IntersectionObserver as the user scrolls,
   and a thin progress rail under the pills tracks whole-page scroll. */
const NAV_HOVER_TONES = {
  teal:     { hover: 'hover:border-teal-400 hover:text-teal-800',       active: 'border-teal-500 text-teal-900 bg-teal-50',        rail: '#0D9488' },
  violet:   { hover: 'hover:border-violet-400 hover:text-violet-800',   active: 'border-violet-500 text-violet-900 bg-violet-50',  rail: '#7C3AED' },
  emerald:  { hover: 'hover:border-emerald-500 hover:text-emerald-800', active: 'border-emerald-600 text-emerald-900 bg-emerald-50', rail: '#059669' },
  rose:     { hover: 'hover:border-rose-400 hover:text-rose-800',       active: 'border-rose-500 text-rose-900 bg-rose-50',        rail: '#E11D48' },
  amber:    { hover: 'hover:border-amber-500 hover:text-amber-800',     active: 'border-amber-600 text-amber-900 bg-amber-50',     rail: '#D97706' },
};

export const PillNav = ({ sections, testidPrefix = 'pill', hoverTone = 'teal' }) => {
  const ids = React.useMemo(() => sections.map((s) => s.id), [sections]);
  const { activeId, progress, scrollToId } = useSectionNav(ids);
  const tone = NAV_HOVER_TONES[hoverTone] || NAV_HOVER_TONES.teal;
  const stripRef = React.useRef(null);
  React.useEffect(() => {
    const strip = stripRef.current;
    const btn = strip?.querySelector('[aria-current="true"]');
    if (!strip || !btn) return;
    const left = btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [activeId]);
  return (
    <div className="lxt-sticky-nav z-30 bg-[#FFF9F1] border-b border-amber-100 shadow-[0_2px_10px_-6px_rgba(0,0,0,0.06)]">
      <div ref={stripRef} className="max-w-4xl mx-auto px-4 md:px-6 py-2.5 flex gap-2 overflow-x-auto no-scrollbar">
        {sections.map((c) => {
          const isActive = c.id === activeId;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => scrollToId(c.id)}
              data-testid={`${testidPrefix}-${c.id}`}
              aria-current={isActive ? 'true' : undefined}
              className={`shrink-0 text-xs font-bold rounded-full px-3.5 py-1.5 border transition-colors ${isActive ? tone.active : `text-gray-600 bg-white border-gray-200 ${tone.hover}`}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      {/* Progress rail — path-tinted, 2px, no layout shift */}
      <div
        className="h-[2px] bg-gray-100/70 relative overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        data-testid={`${testidPrefix}-progress`}
      >
        <span
          className="absolute inset-y-0 left-0 will-change-transform"
          style={{
            width: '100%',
            transform: `scaleX(${progress})`,
            transformOrigin: '0 0',
            background: tone.rail,
            transition: 'transform 90ms linear',
          }}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   QuizYesNoMcq — interactive quiz that handles:
   - fit_questions_yes_no: [{ question }] — student self-assesses Yes/No
   - mcqs: [{ question, options, answer }] — single correct answer (string-match)
   - scoring_guide (optional banking only): { high_fit, medium_fit, low_fit }
   ───────────────────────────────────────────────────────────── */
export const QuizYesNoMcq = ({
  quiz, scoringGuide, testidPrefix = 'quiz',
  reflectionUnscored = false, showExplanations = false, resultLabels, onComplete,
}) => {
  const fit = quiz?.fit_questions_yes_no || [];
  const mcqs = quiz?.mcqs || [];

  const [yn, setYn] = useState({}); // idx -> 'yes' | 'no'
  const [mcq, setMcq] = useState({}); // idx -> option string (chosen)
  const [submittedMcq, setSubmittedMcq] = useState({}); // idx -> true

  const yesCount = useMemo(() => Object.values(yn).filter((v) => v === 'yes').length, [yn]);
  const mcqScore = useMemo(
    () =>
      mcqs.reduce(
        (s, q, i) => (submittedMcq[i] && mcq[i] === q.answer ? s + 1 : s),
        0,
      ),
    [mcqs, mcq, submittedMcq],
  );

  const allYnAnswered = fit.length > 0 && Object.keys(yn).length === fit.length;
  const allMcqAnswered = mcqs.length > 0 && Object.keys(submittedMcq).length === mcqs.length;
  const isDone = (fit.length === 0 || allYnAnswered) && (mcqs.length === 0 || allMcqAnswered);

  const completeRef = React.useRef(false);
  React.useEffect(() => {
    if (!isDone) { completeRef.current = false; return; }
    if (completeRef.current || !onComplete) return;
    completeRef.current = true;
    onComplete({ mcq: { ...mcq }, yn: { ...yn }, mcqScore });
  }, [isDone, onComplete, mcq, yn, mcqScore]);

  if (!fit.length && !mcqs.length) return null;

  /* Compute band — supports either explicit scoring_guide or a sensible default */
  let band = null;
  if (isDone) {
    const totalScore = reflectionUnscored ? mcqScore : yesCount + mcqScore;
    const maxScore = reflectionUnscored ? mcqs.length : fit.length + mcqs.length;
    const pct = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
    const L = resultLabels || {};
    if (pct >= 70) band = { label: L.high || 'High fit', text: scoringGuide?.high_fit, tone: 'mint' };
    else if (pct >= 40) band = { label: L.medium || 'Medium fit', text: scoringGuide?.medium_fit, tone: 'cream' };
    else band = { label: L.low || 'Low fit', text: scoringGuide?.low_fit, tone: 'rose' };
  }

  const reset = () => { setYn({}); setMcq({}); setSubmittedMcq({}); };

  const bandBg = {
    mint:  'border-emerald-300 bg-emerald-50 text-emerald-900',
    cream: 'border-amber-300 bg-amber-50 text-amber-900',
    rose:  'border-rose-300 bg-rose-50 text-rose-900',
  };

  return (
    <div className="space-y-5">
      {/* Score strip */}
      <div className="rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 text-white p-4 md:p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex gap-6">
            {fit.length > 0 && (
              <div>
                <p className="text-[11px] uppercase font-bold tracking-widest text-white/80">{reflectionUnscored ? 'Reflections' : 'Yes'}</p>
                <p className="text-2xl font-extrabold" data-testid={`${testidPrefix}-yes-count`}>
                  {reflectionUnscored ? Object.keys(yn).length : yesCount} <span className="text-base font-medium text-white/80">/ {fit.length}</span>
                </p>
              </div>
            )}
            {mcqs.length > 0 && (
              <div>
                <p className="text-[11px] uppercase font-bold tracking-widest text-white/80">MCQ</p>
                <p className="text-2xl font-extrabold" data-testid={`${testidPrefix}-mcq-score`}>
                  {mcqScore} <span className="text-base font-medium text-white/80">/ {mcqs.length}</span>
                </p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={reset}
            data-testid={`${testidPrefix}-reset`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white"
          >
            <RotateCcw size={14} /> Reset
          </button>
        </div>
      </div>

      {/* Yes/No fit questions */}
      {fit.length > 0 && (
        <div className="space-y-3">
          <p className="text-[11px] uppercase font-bold tracking-widest text-gray-600">{reflectionUnscored ? 'Reflection (Yes / No · not scored)' : 'Quick fit check (Yes / No)'}</p>
          {fit.map((q, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5">
              <p className="font-semibold text-gray-900 mb-3 text-[15px] leading-snug">{q.question}</p>
              <div className="flex gap-2">
                {['yes', 'no'].map((v) => {
                  const isOn = yn[i] === v;
                  const yesTone = v === 'yes'
                    ? (isOn ? 'border-emerald-400 bg-emerald-50 text-emerald-900' : 'border-gray-200 hover:border-emerald-300 bg-white text-gray-700')
                    : (isOn ? 'border-rose-400 bg-rose-50 text-rose-900' : 'border-gray-200 hover:border-rose-300 bg-white text-gray-700');
                  return (
                    <button
                      key={v}
                      type="button"
                      data-testid={`${testidPrefix}-yn-${i}-${v}`}
                      onClick={() => setYn((p) => ({ ...p, [i]: v }))}
                      className={`px-5 py-2 rounded-full text-sm font-semibold border-2 transition-all ${yesTone}`}
                    >
                      {v === 'yes' ? 'Yes' : 'No'}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MCQs */}
      {mcqs.length > 0 && (
        <div className="space-y-3">
          <p className="text-[11px] uppercase font-bold tracking-widest text-gray-600">Multiple-choice questions</p>
          {mcqs.map((q, i) => {
            const chosen = mcq[i];
            const isSubmitted = !!submittedMcq[i];
            return (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6">
                <p className="font-semibold text-gray-900 mb-4 text-[15px] leading-snug">Q{i + 1}. {q.question}</p>
                <div className="space-y-2">
                  {(q.options || []).map((opt, oi) => {
                    const isChosen = chosen === opt;
                    const isCorrect = isSubmitted && opt === q.answer;
                    const isWrong = isSubmitted && isChosen && opt !== q.answer;
                    return (
                      <button
                        key={oi}
                        type="button"
                        data-testid={`${testidPrefix}-mcq-${i}-${oi}`}
                        disabled={isSubmitted}
                        onClick={() => setMcq((p) => ({ ...p, [i]: opt }))}
                        className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm md:text-[15px] transition-all flex items-start gap-3 ${
                          isCorrect ? 'border-emerald-400 bg-emerald-50 text-emerald-900'
                            : isWrong ? 'border-rose-400 bg-rose-50 text-rose-900'
                            : isChosen ? 'border-violet-400 bg-violet-50 text-violet-900'
                            : 'border-gray-200 hover:border-gray-300 bg-white text-gray-800'
                        } ${isSubmitted ? 'cursor-default' : 'cursor-pointer'}`}
                      >
                        <span className="mt-0.5">
                          {isSubmitted
                            ? (opt === q.answer
                                ? <CheckCircle2 size={18} className="text-emerald-600" />
                                : (isChosen ? <XCircle size={18} className="text-rose-600" /> : <span className="w-[18px] h-[18px] inline-block rounded-full border border-gray-300" />))
                            : <span className={`w-[18px] h-[18px] inline-block rounded-full border ${isChosen ? 'bg-violet-500 border-violet-500' : 'border-gray-300'}`} />}
                        </span>
                        <span className="flex-1">{opt}</span>
                      </button>
                    );
                  })}
                </div>
                {!isSubmitted ? (
                  <button
                    type="button"
                    disabled={chosen === undefined}
                    onClick={() => setSubmittedMcq((p) => ({ ...p, [i]: true }))}
                    data-testid={`${testidPrefix}-mcq-${i}-submit`}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Submit answer
                  </button>
                ) : (
                  <>
                    {chosen !== q.answer && (
                      <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
                        <span className="font-semibold">Correct answer:</span> {q.answer}
                      </div>
                    )}
                    {showExplanations && q.explanation && (
                      <div className="mt-3 rounded-xl bg-violet-50 border border-violet-200 p-3 text-sm text-violet-900" data-testid={`${testidPrefix}-mcq-${i}-explanation`}>
                        <span className="font-semibold">Why:</span> {q.explanation}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mini task — banking-only */}
      {quiz?.mini_task && (
        <InfoCard label="Mini task — try it" tone="cream">
          {quiz.mini_task}
        </InfoCard>
      )}

      {/* Final band */}
      {band && (
        <div className={`rounded-3xl border-2 p-6 text-center shadow-sm ${bandBg[band.tone]}`} data-testid={`${testidPrefix}-result`}>
          <Trophy className="mx-auto mb-2" size={28} />
          <p className="text-xs uppercase font-bold tracking-widest mb-1">{resultLabels ? 'Knowledge check result' : 'Final result'}</p>
          <p className="text-xl md:text-2xl font-extrabold mb-1">{band.label}</p>
          {band.text && <p className="text-sm md:text-base max-w-xl mx-auto leading-relaxed">{band.text}</p>}
        </div>
      )}
    </div>
  );
};
